import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Info, AlertTriangle, ChevronDown, ChevronUp, Clock, ShieldCheck } from "lucide-react";

export interface FinancePageHeaderProps {
  eyebrow?: string;
  eyebrowIcon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  updatedAt?: string | Date | null;
  badgeText?: string;
  primaryAction?: {
    label: string;
    icon?: React.ComponentType<{ className?: string }>;
    onClick: () => void;
    disabled?: boolean;
    variant?: "default" | "outline" | "secondary";
  };
  secondaryActions?: React.ReactNode;
  note?: {
    title?: string;
    message: string;
    missingSources?: string[];
    type?: "warning" | "info" | "amber";
  };
}

function formatDateTime(value?: string | Date | null) {
  if (!value) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString("es-ES");
}

export function FinancePageHeader({
  eyebrow = "Finanzas y Contabilidad",
  eyebrowIcon: EyebrowIcon = ShieldCheck,
  title,
  description,
  updatedAt,
  badgeText,
  primaryAction,
  secondaryActions,
  note,
}: FinancePageHeaderProps) {
  const [noteOpen, setNoteOpen] = useState(false);
  const formattedDate = formatDateTime(updatedAt);

  return (
    <div className="space-y-3 pb-1 border-b border-border/40">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1 min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <EyebrowIcon className="h-3.5 w-3.5 text-primary/80" />
            <span>{eyebrow}</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">{title}</h1>
          {description && (
            <p className="max-w-4xl text-sm text-muted-foreground leading-relaxed">{description}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {formattedDate && (
            <Badge variant="outline" className="gap-1 px-2.5 py-1 text-xs font-normal text-muted-foreground bg-muted/30">
              <Clock className="h-3 w-3" />
              <span>Actualizado {formattedDate}</span>
            </Badge>
          )}
          {badgeText && (
            <Badge variant="secondary" className="px-2.5 py-1 text-xs">
              {badgeText}
            </Badge>
          )}
          {secondaryActions}
          {primaryAction && (
            <Button
              variant={primaryAction.variant || "default"}
              size="sm"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              className="gap-2 shadow-sm font-medium"
            >
              {primaryAction.icon && <primaryAction.icon className="h-4 w-4" />}
              <span>{primaryAction.label}</span>
            </Button>
          )}
        </div>
      </div>

      {note && (
        <div className="rounded-lg border border-amber-200/80 bg-amber-50/50 p-3 text-xs text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-200 transition-all">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 font-medium">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
              <span>{note.title || "Nota de cobertura financiera"}</span>
            </div>
            <button
              type="button"
              onClick={() => setNoteOpen(!noteOpen)}
              className="flex items-center gap-1 text-amber-800 dark:text-amber-300 hover:underline focus:outline-none focus:ring-1 focus:ring-amber-500 rounded px-1"
              aria-expanded={noteOpen}
            >
              <span>{noteOpen ? "Ocultar detalles" : "Detalle de cobertura"}</span>
              {noteOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          </div>

          <p className="mt-1 text-amber-900/90 dark:text-amber-300/90">{note.message}</p>

          {noteOpen && note.missingSources && note.missingSources.length > 0 && (
            <div className="mt-2.5 pt-2 border-t border-amber-200/60 dark:border-amber-900/40 text-[11px] text-amber-800 dark:text-amber-400">
              <span className="font-semibold">Fuentes ausentes en este resumen: </span>
              <span>{note.missingSources.join(", ")}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
